import React, { useEffect, useState } from "react";
import { Gift, Trophy, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const CAMPAIGN_ID = "milestone_giftcard_v1";
const DRAW_DATE = "2026-08-04"; // 14 days after 2026-07-21 send

/**
 * $25 gift-card giveaway operator panel.
 * - Shows live entry counts (signups + referral bonuses).
 * - "Draw winner" button after DRAW_DATE picks weighted-random from entries.
 */
export default function GiveawayCard() {
  const [stats, setStats] = useState(null);
  const [winner, setWinner] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/giveaway/${CAMPAIGN_ID}/entries`, { headers: authHeaders() });
      setStats(data);
      const w = (data.entries_by_email || []).find(x => x.winner);
      if (w) setWinner(w);
    } catch (_) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const canDraw = today >= DRAW_DATE;
  const daysUntil = Math.max(0, Math.ceil((new Date(DRAW_DATE) - new Date()) / (1000 * 60 * 60 * 24)));

  const draw = async () => {
    if (!window.confirm(`Draw the $25 giveaway winner now? This is idempotent — running twice gives the same winner.`)) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/giveaway/${CAMPAIGN_ID}/draw`, {}, { headers: authHeaders() });
      setWinner(data.winner);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Draw failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="fl-card p-6 mt-6" data-testid="giveaway-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
            <Gift className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="fl-display text-xl font-bold text-white">$25 Milestone Giveaway</h2>
            <p className="text-white/50 text-sm mt-0.5">
              Sent {stats ? `to ${stats.total_invites} outreach recipients` : "…"} · Draw date <strong className="text-emerald-300">{DRAW_DATE}</strong> {!canDraw && <span>· {daysUntil} day{daysUntil === 1 ? "" : "s"} away</span>}
            </p>
          </div>
        </div>
        <button onClick={load} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="giveaway-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="fl-card p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Invites sent</div>
            <div className="text-white text-2xl font-black mt-0.5">{stats.total_invites}</div>
          </div>
          <div className="fl-card p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Signed up</div>
            <div className="text-white text-2xl font-black mt-0.5">{stats.signed_up}</div>
          </div>
          <div className="fl-card p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Total entries</div>
            <div className="text-emerald-400 text-2xl font-black mt-0.5">{stats.total_entries}</div>
          </div>
        </div>
      )}

      {winner ? (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-400/30 flex items-start gap-3" data-testid="giveaway-winner">
          <Trophy className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-emerald-200 text-xs uppercase tracking-widest font-semibold">Winner drawn 🎉</div>
            <div className="text-white font-bold text-lg mt-0.5 truncate">{winner.email}</div>
            <div className="text-white/60 text-sm mt-0.5">Had {winner.entries || 1} entr{(winner.entries || 1) === 1 ? "y" : "ies"} — congrats! Reach out to send them the $25.</div>
          </div>
        </div>
      ) : (
        <button
          onClick={draw}
          disabled={!canDraw || busy}
          className="fl-btn"
          data-testid="giveaway-draw-btn"
          title={canDraw ? "Pick a weighted-random winner from all raffle entries." : `Available on ${DRAW_DATE}`}
        >
          <Trophy className="w-4 h-4" /> {busy ? "Drawing…" : canDraw ? "Draw the $25 winner now" : `Draw in ${daysUntil} day${daysUntil === 1 ? "" : "s"}`}
        </button>
      )}
      {err && <div className="text-red-300 text-sm mt-2" data-testid="giveaway-err">{err}</div>}
    </section>
  );
}
